import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { listHostedEvents, listJoinedEvents } from "@/lib/events";
import { useAuth } from "@/context/AuthContext";
import EventCard from "@/components/EventCard";
import { Settings, Plus } from "lucide-react";

export default function MyEventsPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "joined" ? "joined" : "hosting";

  const [hosting, setHosting] = useState([]);
  const [joined, setJoined] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    const ctrl = new AbortController();
    setLoading(true);
    Promise.all([listHostedEvents(user.id), listJoinedEvents(user.id)])
      .then(([h, j]) => {
        if (ctrl.signal.aborted) return;
        setHosting(h);
        setJoined(j);
      })
      .catch(() => { if (!ctrl.signal.aborted) { setHosting([]); setJoined([]); } })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [user?.id]);

  const list = tab === "hosting" ? hosting : joined;

  const heading = useMemo(
    () => (tab === "hosting" ? "Events you're hosting." : "Events you've joined."),
    [tab]
  );

  return (
    <div className="bg-paper min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-20">
        <div className="flex flex-col gap-4 md:gap-6 md:flex-row md:items-end md:justify-between mb-10">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-ink">My events</span>
            <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight mt-2 leading-none">
              {heading}
            </h1>
          </div>
          <Link
            to="/create"
            data-testid="my-events-create"
            className="self-start inline-flex items-center gap-2 bg-neon text-ink px-5 py-3 text-xs font-bold uppercase tracking-widest hover:bg-paper transition-colors"
          >
            <Plus size={14} strokeWidth={2.5} /> Post an event
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-ink/15 mb-10" data-testid="my-events-tabs">
          {[
            { key: "hosting", label: `Hosting · ${hosting.length}` },
            { key: "joined", label: `Joined · ${joined.length}` },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`my-events-tab-${t.key}`}
              onClick={() => {
                const next = new URLSearchParams(params);
                if (t.key === "hosting") next.delete("tab");
                else next.set("tab", t.key);
                setParams(next);
              }}
              className={`relative px-6 py-4 text-xs uppercase tracking-widest transition-colors ${
                tab === t.key ? "text-ink" : "text-ink/50 hover:text-ink"
              }`}
            >
              {t.label}
              {tab === t.key && (
                <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-neon" />
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="border border-ink/15 h-[420px] bg-[#101010] animate-pulse" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <EmptyState tab={tab} />
        ) : (
          <div data-testid="my-events-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {list.map((e, i) => (
              <div key={e.id} className="relative">
                <EventCard event={e} index={i} />
                {tab === "hosting" && (
                  <Link
                    to={`/events/${e.id}/manage`}
                    data-testid={`manage-event-${e.id}`}
                    onClick={(ev) => ev.stopPropagation()}
                    className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 bg-paper/90 border border-ink/15 hover:border-neon hover:text-ink text-ink px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors"
                  >
                    <Settings size={12} strokeWidth={2} /> Manage
                  </Link>
                )}
                {tab === "joined" && e.my_status && e.my_status !== "approved" && (
                  <span
                    className={`absolute top-3 right-3 z-10 px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${
                      e.my_status === "pending"
                        ? "bg-paper text-ink"
                        : "bg-destructive text-ink"
                    }`}
                  >
                    {e.my_status}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState({ tab }) {
  return (
    <div className="border border-dashed border-ink/15 p-12 text-center">
      <p className="font-display text-2xl text-ink">
        {tab === "hosting" ? "You haven't hosted anything yet." : "You haven't joined anything yet."}
      </p>
      <p className="text-ink/60 mt-2 mb-6">
        {tab === "hosting" ? "Post the first thing in your city." : "Browse the feed and tap join."}
      </p>
      <Link
        to={tab === "hosting" ? "/create" : "/feed"}
        data-testid="my-events-empty-cta"
        className="inline-block bg-neon text-ink px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-paper transition-colors"
      >
        {tab === "hosting" ? "Post an event" : "Explore the feed"}
      </Link>
    </div>
  );
}
